import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import numeral from 'numeraljs'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import ConfirmationButtons from './confirmation-buttons'
import Heading from './heading'
import { logActionAsync } from '../store/actions'
import styles from '../component-styles/campaign-code-generator.module.scss'

interface WarningModalProps {
  onYes: () => void
  onNo: () => void
  type: WarningTypes
  single: boolean
  length?: number
  maxLength?: number
  url: string[]
}

export default function WarningModal({
  onYes,
  onNo,
  type,
  single,
  length = 0,
  maxLength = 0,
  url,
}: WarningModalProps): React.ReactElement {
  const dispatch = useDispatch()

  const action = useMemo(() => {
    switch (type) {
      case 'invalid-length':
        return 'max-url-length'
      case 'invalid-query-length':
        return 'max-query-length'
      case 'no-url':
        return 'missing-landing'
      case 'has-anchor':
        return 'has-anchor'
      default:
        return ''
    }
  }, [type])

  const noLabel = useMemo(() => {
    switch (type) {
      case 'invalid-length':
        return 'Back and edit landing page URL'
      case 'invalid-query-length':
        return 'Back and edit parameters'
      case 'no-url':
        return 'Back and add landing page URL'
      case 'has-anchor':
        return 'Back and update landing page'
      default:
        return ''
    }
  }, [type])

  const showCreateButton = useMemo(() => {
    return !(type === 'invalid-length' || type === 'invalid-query-length')
  }, [type])

  return (
    <Modal
      active
      toggleActive={() => {
        onNo()
      }}
      type="white"
      superNarrow
      isWarning
    >
      <ModalHead>
        <Heading type={3} align="left">
          {type === 'invalid-query-length' || type === 'has-anchor'
            ? `Error creating ${single ? 'link' : 'links'}`
            : `${
                single ? 'This link is too long' : 'These links are too long'
              }?`}
        </Heading>
      </ModalHead>
      <ModalBody className={styles.modalBody}>
        {type === 'has-anchor' && (
          <>
            <p>
              Your link contains a page anchor, '#', which will be moved to the
              end to make this a valid link.
            </p>
            <p>
              <a
                target="_blank"
                rel="noreferrer"
                href="https://support.uplifter.ai/hc/en-us/articles/4469479289873-Can-I-use-anchor-tags-or-in-my-landing-page-URL-with-query-string-parameters-utms-"
              >
                {' '}
                Find out more.
              </a>
            </p>
          </>
        )}
        {type === 'no-url' && (
          <>
            <p>
              You didn’t enter a landing page URL, users could be directed to an
              error page.
            </p>
            <p>
              You should only create this link if you plan on adding a landing
              page URL into the{' '}
              <span className={styles.boxed}>Track&gt;View</span> table before
              the campaign goes live.
            </p>
          </>
        )}
        {single && type === 'invalid-length' && (
          <>
            <p>
              The resulting link will be too long to work in most web browsers
              or marketing platforms.
            </p>
            <p>
              Your resulting link is{' '}
              <strong>
                {numeral(length - maxLength).format('0,0')} characters longer
              </strong>{' '}
              than the maxium recommended ({numeral(maxLength).format('0,0')}{' '}
              characters).
            </p>
            <p>
              Try <strong>changing the landing page</strong> to a shorter url.
            </p>
          </>
        )}
        {!single && type === 'invalid-length' && (
          <>
            <p>
              The resulting links will be too long to work in most web browsers
              or marketing platforms.
            </p>
            <p>
              At least one of your links is longer than the recommended{' '}
              {numeral(maxLength).format('0,0')} characters.
            </p>
            <p>
              Try <strong>changing the landing page</strong> to a shorter url.
            </p>
          </>
        )}
        {single && type === 'invalid-query-length' && (
          <>
            <p>
              The query string length will be too long to work in most web
              browsers or marketing platforms.
            </p>
            <p>
              Your query string is{' '}
              <strong>
                {numeral(length - maxLength).format('0,0')} characters longer
              </strong>{' '}
              than the recommended ({numeral(maxLength).format('0,0')}{' '}
              characters).
            </p>
            <p>
              Type <strong>less characters into the parameter values</strong>{' '}
              for a shorter string.
            </p>
            <p>
              For help email{' '}
              <a href="mailto:support@uplifter.ai">support@uplifter.ai</a>
            </p>
          </>
        )}
        {!single && type === 'invalid-query-length' && (
          <>
            <p>
              The query string length will be too long to work in most web
              browsers or marketing platforms.
            </p>
            <p>
              At least one of your query links is longer than the recommended{' '}
              {numeral(maxLength).format('0,0')} characters.
            </p>
            <p>
              Type <strong>less characters into the parameter values</strong>{' '}
              for a shorter string.
            </p>
            <p>
              For help email{' '}
              <a href="mailto:support@uplifter.ai">support@uplifter.ai</a>
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={!showCreateButton}
          hideDisabled={!showCreateButton}
          type={type === 'has-anchor' ? 'simple' : 'warning'}
          onYes={() => {
            onYes()
            const actionData = {
              action: `${action}-ignore`,
              longDescription: JSON.stringify({
                url,
              }),
              websiteSection: 'track-create',
            }
            dispatch(logActionAsync(actionData))
          }}
          onNo={(): void => {
            onNo()
            const actionData = {
              action: `${action}-update`,
              longDescription: JSON.stringify({
                url,
              }),
              websiteSection: 'track-create',
            }
            dispatch(logActionAsync(actionData))
          }}
          yesLabel="Yes, create"
          noLabel={noLabel}
        />
      </ModalFooter>
    </Modal>
  )
}
